import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Menu,
  Bell,
  LogOut,
  Settings,
  ChevronRight,
  X,
  HardHat,
  User,
  MapPin,
  IndianRupee,
  PanelLeftClose,
  PanelLeft,
  Layers,
} from "lucide-react";

import { supabase } from "../lib/supabase";
import { toast } from "react-hot-toast";

const navItems = [
  { name: "Project Overview", path: "/", icon: <LayoutDashboard size={20} /> },
  { name: "Site Inventory", path: "/sites", icon: <MapPin size={20} /> },
  { name: "Supervisor", path: "/supervisor", icon: <User size={20} /> },
  {
    name: "Personal Expenses",
    path: "/personal-expenses",
    icon: <IndianRupee size={20} />,
  },
  {
    name: "Daily Logs",
    path: "/daily-logs",
    icon: <LayoutDashboard size={20} />,
  },
  {
    name: "Invoice Management",
    path: "/invoices",
    icon: <IndianRupee size={20} />,
  },
  { name: "MB", path: "/mb", icon: <IndianRupee size={20} /> },
  { name: "Portal Settings", path: "/settings", icon: <Settings size={20} /> },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [businessName, setBusinessName] = useState("Protech Construction");
  const [adminDisplayName, setAdminDisplayName] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    fetchBusinessSettings();
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const fetchBusinessSettings = async () => {
    try {
      const { data } = await supabase
        .from("settings")
        .select("shop_name, admin_name")
        .maybeSingle();
      if (data) {
        setBusinessName(data.shop_name);
        setAdminDisplayName(data.admin_name);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Logout failed");
    } else {
      toast.success("Signed out successfully");
      navigate("/login");
    }
  };

  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Header & Toggle */}
      <div
        className={`p-6 flex items-center justify-between ${isCollapsed && !isMobile ? "justify-center" : ""}`}
      >
        <div
          className={`flex items-center gap-2.5 transition-opacity duration-300 ${isCollapsed && !isMobile ? "hidden" : "opacity-100"}`}
        >
          <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white shadow-xs shrink-0">
            <img
              src="/protech.png"
              alt="Logo"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col whitespace-nowrap overflow-hidden text-left">
            <span className="text-sm font-black text-slate-900 uppercase tracking-tight leading-none">
              PRO<span className="text-emerald-700">TECH</span>
            </span>
            <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest mt-1">
              CONSTRUCTION
            </span>
          </div>
        </div>

        {/* Collapse Toggle (Desktop only) */}
        {!isMobile && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 text-stone-400 hover:bg-stone-100 rounded-lg transition-colors"
          >
            {isCollapsed ? (
              <PanelLeft size={18} />
            ) : (
              <PanelLeftClose size={18} />
            )}
          </button>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 px-3 space-y-1.5 mt-4 overflow-y-auto overflow-x-hidden">
        <p
          className={`px-4 text-[9px] font-bold text-stone-400 uppercase tracking-[0.2em] mb-3 transition-all ${isCollapsed && !isMobile ? "opacity-0 h-0" : "opacity-100"}`}
        >
          Operations Control
        </p>

        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.name}
              to={item.path}
              title={isCollapsed ? item.name : ""}
              className={`flex items-center px-4 py-3 rounded-xl transition-all group relative ${
                isActive
                  ? "bg-slate-800 text-white shadow-sm"
                  : "text-stone-500 hover:bg-stone-100/80 hover:text-slate-900"
              } ${isCollapsed && !isMobile ? "justify-center" : ""}`}
            >
              <div className="flex items-center gap-4 shrink-0">
                <span
                  className={`${isActive ? "text-emerald-400" : "text-stone-400 group-hover:text-slate-700"} transition-colors`}
                >
                  {item.icon}
                </span>
                <span
                  className={`font-semibold text-xs uppercase tracking-wider whitespace-nowrap transition-all duration-300 ${isCollapsed && !isMobile ? "opacity-0 w-0 translate-x-10" : "opacity-100 w-auto translate-x-0"}`}
                >
                  {item.name}
                </span>
              </div>

              {!isCollapsed || isMobile
                ? isActive && (
                    <ChevronRight
                      size={12}
                      className="text-emerald-400 ml-auto"
                    />
                  )
                : null}
            </Link>
          );
        })}
      </nav>

      {/* Action Footer Segment */}
      <div
        className={`px-3 pb-6 mt-auto ${isCollapsed && !isMobile ? "flex justify-center" : ""}`}
      >
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-stone-400 hover:bg-stone-100 hover:text-slate-900 transition-all font-bold text-xs uppercase tracking-wider"
        >
          <LogOut size={18} className="shrink-0 text-stone-400" />
          <span
            className={`transition-all duration-300 ${isCollapsed && !isMobile ? "opacity-0 w-0" : "opacity-100 w-auto"}`}
          >
            Sign Out
          </span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#faf9f6] font-sans text-slate-700 overflow-hidden">
      {/* --- Desktop Sidebar --- */}
      <aside
        className={`bg-white hidden md:flex flex-col border-r border-stone-200/80 shadow-2xs transition-all duration-500 ease-in-out ${
          isCollapsed ? "w-24" : "w-72"
        }`}
      >
        <SidebarContent />
      </aside>

      {/* --- Mobile Sidebar Drawer --- */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-xs"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col shadow-2xl animate-in slide-in-from-left duration-300">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-stone-400 hover:text-slate-700"
              >
                <X size={22} />
              </button>
            </div>
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* --- Main Dashboard View --- */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 lg:h-20 bg-white/95 backdrop-blur-md border-b border-stone-200/80 flex items-center justify-between px-4 lg:px-10 z-10 shadow-2xs">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 text-slate-700 hover:bg-stone-100 rounded-lg"
            >
              <Menu size={22} />
            </button>

            <div className="flex flex-col overflow-hidden text-left">
              <h1 className="text-xs lg:text-sm font-bold text-slate-900 uppercase tracking-wider truncate">
                {location.pathname === "/"
                  ? "Site Monitoring"
                  : location.pathname.replace("/", "").replace(/-/g, " ")}
              </h1>
              <p className="hidden sm:block text-[9px] text-stone-400 font-bold uppercase tracking-widest mt-0.5">
                Contractor Management Portal
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 lg:gap-5">
            <button className="p-2 text-stone-400 hover:bg-stone-100 hover:text-slate-800 rounded-lg transition-all relative">
              <Bell size={18} />
              <span className="absolute top-2 right-2 h-1.5 w-1.5 bg-emerald-700 rounded-full border border-white"></span>
            </button>

            <div className="flex items-center gap-3 pl-3 lg:pl-5 border-l border-stone-200">
              <div className="text-right hidden sm:block">
                <p className="text-xs font-bold text-slate-800 leading-tight">
                  {adminDisplayName}
                </p>
                <p className="text-[9px] font-bold text-emerald-800 uppercase tracking-widest mt-0.5">
                  Vendor Admin
                </p>
              </div>
              <div className="h-9 w-9 lg:h-10 lg:w-10 rounded-xl bg-stone-100 flex items-center justify-center overflow-hidden shadow-2xs border border-stone-200 transition-transform active:scale-95 cursor-pointer">
                <User className="h-4 w-4 lg:h-5 lg:w-5 text-slate-600" />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-[#faf9f6]">
          <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
